import { useContext } from 'react';
import { Context } from '../../../App';
import buildHansenSketchModel from './hansenSketchModel';
import HansenSketchEmptyState from './HansenSketchEmptyState';

const hasValue = (v) => (v || v === 0) && v !== '';
const fmt = (v, unit) => (hasValue(v) ? `${Number(v)}${unit ? ` ${unit}` : ''}` : '-');

export default function HansenSketchFrontView({ model, variant }) {
  const { theme: appTheme } = useContext(Context);
  const theme = variant === 'pdf-light' ? 'light' : appTheme;
  const sketch = model || buildHansenSketchModel();
  const { raw, frame, footing, front } = sketch;
  const { VW, VH, yGround, yBottom, xInset, frameStyle } = frame;
  const cx = footing.cx;
  const concretePatternId = theme === 'light' ? 'hf-concrete-light' : 'hf-concrete-dark';
  const soilTint = theme === 'light' ? '#f7f2e6' : '#2f2f2f';
  const showAnnotations = !front.noInputs;
  const stemRenderW = front.noInputs ? front.stemWEmpty : front.stemW;
  const stemIsOutOfBounds = !front.isStemCompletelyOnBase;
  const stemLeft = front.stemCx - stemRenderW / 2;
  const stemRight = front.stemCx + stemRenderW / 2;
  const stemOverflowLeftX = stemLeft;
  const stemOverflowLeftW = Math.max(0, Math.min(stemRight, footing.left) - stemLeft);
  const stemOverflowRightX = Math.max(stemLeft, footing.right);
  const stemOverflowRightW = Math.max(0, stemRight - stemOverflowRightX);
  const stemFill = stemIsOutOfBounds ? (theme === 'light' ? '#f8c7cc' : '#6b2b2b') : `url(#${concretePatternId})`;
  const stemStroke = stemIsOutOfBounds ? '#c62828' : '#666';
  const badgeRightGap = 5;
  const badgeW = 154;
  const badgeH = 18;
  const badgeStackGap = 4;
  const statusBadge = {
    w: badgeW,
    h: badgeH,
    x: VW - xInset - badgeW - badgeRightGap,
    y: yGround + 60,
    fill: front.isStemCompletelyOnBase ? (theme === 'light' ? '#e8f5e9' : '#274a2f') : theme === 'light' ? '#ffebee' : '#5a1f27',
    stroke: front.isStemCompletelyOnBase ? '#2e7d32' : '#c62828',
    text: front.isStemCompletelyOnBase ? 'Stem is on the base' : 'Stem is not on the base',
  };
  const clipBadgeText = front.displayClippedTokens.length ? `Display clipped: ${front.displayClippedTokens.join(' / ')}` : '';
  const clipBadge = {
    w: badgeW,
    h: badgeH,
    x: VW - xInset - badgeW - badgeRightGap,
    y: statusBadge.y + statusBadge.h + badgeStackGap,
    fill: theme === 'light' ? '#fff3e0' : '#5a4720',
    stroke: '#ef6c00',
    text: clipBadgeText,
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', paddingInline: 8 }}>
      <div style={frameStyle}>
        <svg
          viewBox={`0 0 ${VW} ${VH}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ display: 'block', width: '100%', height: '100%', maxWidth: '100%' }}
        >
          <defs>
            <marker id="hf-arrow" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
              <path d="M0,0 L7,3.5 L0,7 Z" fill="currentColor" />
            </marker>
            <marker id="hf-arrow-up" markerWidth="7" markerHeight="7" refX="2" refY="3.5" orient="auto">
              <path d="M7,0 L0,3.5 L7,7 Z" fill="currentColor" />
            </marker>
            <marker id="hf-arrow-open-r" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
              <path d="M0,0 L8,4 L0,8" fill="none" stroke="currentColor" strokeWidth="1.2" />
            </marker>
            <marker id="hf-arrow-open-l" markerWidth="8" markerHeight="8" refX="2" refY="4" orient="auto">
              <path d="M8,0 L0,4 L8,8" fill="none" stroke="currentColor" strokeWidth="1.2" />
            </marker>
            <marker id="hf-arrow-open-r-green" markerWidth="5" markerHeight="5" refX="4" refY="2.5" orient="auto">
              <path d="M0,0 L5,2.5 L0,5" fill="none" stroke="#2e7d32" strokeWidth="1" />
            </marker>
            <marker id="hf-arrow-open-l-green" markerWidth="5" markerHeight="5" refX="1" refY="2.5" orient="auto">
              <path d="M5,0 L0,2.5 L5,5" fill="none" stroke="#2e7d32" strokeWidth="1" />
            </marker>
            <marker id="hf-arrow-blue" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
              <path d="M0,0 L7,3.5 L0,7 Z" fill="#1976d2" />
            </marker>
            <marker id="hf-arrow-up-blue" markerWidth="7" markerHeight="7" refX="2" refY="3.5" orient="auto">
              <path d="M7,0 L0,3.5 L7,7 Z" fill="#1976d2" />
            </marker>
            <pattern id="hf-soil" patternUnits="userSpaceOnUse" width="16" height="16">
              <circle cx="2" cy="2" r="1" fill="currentColor" opacity="0.2" />
              <circle cx="8" cy="6" r="1" fill="currentColor" opacity="0.2" />
              <circle cx="6" cy="13" r="1" fill="currentColor" opacity="0.2" />
            </pattern>
            <pattern id="hf-concrete-light" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
              <rect width="6" height="6" fill="#ececec" />
              <line x1="0" y1="0" x2="0" y2="6" stroke="#9e9e9e" strokeWidth="0.6" />
            </pattern>
            <pattern id="hf-concrete-dark" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
              <rect width="6" height="6" fill="#3f3f3f" />
              <line x1="0" y1="0" x2="0" y2="6" stroke="#6a6a6a" strokeWidth="0.6" />
            </pattern>
            <pattern id="hf-overflow-hatch" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
              <rect width="6" height="6" fill="rgba(198,40,40,0.12)" />
              <line x1="0" y1="0" x2="0" y2="6" stroke="#c62828" strokeWidth="0.9" />
            </pattern>
          </defs>

          <style>{`
            .hf-ln { stroke: currentColor; stroke-width: 1.1; fill: none; }
            .hf-dim { stroke: currentColor; stroke-width: 1; fill: none; }
            .hf-tx { font-family: system-ui, -apple-system, "Segoe UI", Roboto, Arial; fill: currentColor; }
            .hf9 { font-size: 9px; }
            .hf12 { font-size: 12px; }
            .hf13 { font-size: 13px; }
            .hf-muted { opacity: 0.9; }
            .hf-axis { stroke-dasharray: 5 4; opacity: 0.75; }
            .hf-ecc { stroke-width: 1; fill: none; opacity: 0.95; }
            .hf-gwt { stroke: #1976d2; stroke-width: 1.5; stroke-dasharray: 6 4; }
            .hf-gwt-fill { fill: #1976d2; opacity: 0.08; }
          `}</style>

          <rect x={xInset} y={yGround} width={VW - xInset * 2} height={yBottom - yGround} fill={soilTint} stroke="currentColor" />
          <rect x={xInset} y={yGround} width={VW - xInset * 2} height={yBottom - yGround} fill="url(#hf-soil)" />

          {showAnnotations && front.hasGwt && (
            <>
              <rect className="hf-gwt-fill" x={xInset} y={front.yGwt} width={VW - xInset * 2} height={Math.max(0, yBottom - front.yGwt)} />
              <line className="hf-gwt" x1={xInset} y1={front.yGwt} x2={VW - xInset} y2={front.yGwt} />
              <line
                x1="58"
                y1={yGround}
                x2="58"
                y2={front.yGwt}
                stroke="#1976d2"
                strokeWidth="1"
                markerStart="url(#hf-arrow-up-blue)"
                markerEnd="url(#hf-arrow-blue)"
              />
              <text className="hf-tx hf12" x={66} y={front.yGwt - 7 < yGround + 12 ? front.yGwt + 14 : front.yGwt - 7} fill="#1976d2">
                GWT = {fmt(raw.gwt, 'm')}
                {front.gwtClampedBottom ? ' (below view)' : ''}
              </text>
            </>
          )}

          <rect x={front.stemCx - stemRenderW / 2} y={front.yStemTop} width={stemRenderW} height={front.stemH} fill={stemFill} stroke={stemStroke} />
          <rect x={footing.left} y={footing.top} width={footing.width} height={footing.height} fill={`url(#${concretePatternId})`} stroke="#666" />
          {showAnnotations && stemIsOutOfBounds && (
            <>
              {stemOverflowLeftW > 0 && (
                <rect x={stemOverflowLeftX} y={front.yStemTop} width={stemOverflowLeftW} height={front.stemH} fill="url(#hf-overflow-hatch)" />
              )}
              {stemOverflowRightW > 0 && (
                <rect x={stemOverflowRightX} y={front.yStemTop} width={stemOverflowRightW} height={front.stemH} fill="url(#hf-overflow-hatch)" />
              )}
            </>
          )}
          <line className="hf-ln hf-axis" x1={cx} y1={yGround} x2={cx} y2={front.yFootBottom} />
          {showAnnotations && front.showEx && <line className="hf-ln hf-axis" x1={front.stemCx} y1={front.yStemTop} x2={front.stemCx} y2={front.yFootBottom} />}

          {showAnnotations && front.showEx && (
            <>
              <line
                className="hf-ecc"
                x1={front.stemCx}
                y1={front.yFootTop - 14}
                x2={cx}
                y2={front.yFootTop - 14}
                markerStart="url(#hf-arrow-open-l-green)"
                markerEnd="url(#hf-arrow-open-r-green)"
                stroke="#2e7d32"
              />
              <text className="hf-tx hf12" x={(front.stemCx + cx) / 2} y={front.yFootTop - 20} textAnchor="middle" fill="#2e7d32">
                e<tspan baselineShift="-8%">x</tspan> = {fmt(raw.eccentricityX, 'm')}
              </text>
            </>
          )}

          {showAnnotations && front.showDf && (
            <>
              <line
                className="hf-dim"
                x1={footing.left - 70}
                y1={yGround}
                x2={footing.left - 70}
                y2={front.yFootBottom}
                markerStart="url(#hf-arrow-up)"
                markerEnd="url(#hf-arrow)"
              />
              <text className="hf-tx hf13" x={footing.left - 78} y={(yGround + front.yFootBottom) / 2} textAnchor="end">
                D<tspan baselineShift="sub">f</tspan> = {fmt(raw.df, 'm')}
              </text>
            </>
          )}

          {showAnnotations && front.showDw && (
            <>
              <line
                className="hf-dim"
                x1={footing.left - 70}
                y1={front.yFootBottom}
                x2={footing.left - 70}
                y2={front.yGwt}
                markerStart="url(#hf-arrow-up)"
                markerEnd="url(#hf-arrow)"
              />
              <text className="hf-tx hf13" x={footing.left - 78} y={(front.yFootBottom + front.yGwt) / 2} textAnchor="end">
                d<tspan baselineShift="sub">w</tspan> = {fmt(front.dwValue, 'm')}
              </text>
            </>
          )}

          {showAnnotations && front.showB && (
            <>
              <line
                className="hf-dim"
                x1={footing.left}
                y1={front.yFootBottom + 38}
                x2={footing.right}
                y2={front.yFootBottom + 38}
                markerStart="url(#hf-arrow-open-l)"
                markerEnd="url(#hf-arrow-open-r)"
              />
              <text className="hf-tx hf13" x={cx} y={front.yFootBottom + 56} textAnchor="middle">
                B = {fmt(raw.fndWidth, 'm')}
              </text>
            </>
          )}

          {showAnnotations && front.showV && (
            <>
              <line
                className="hf-ln"
                x1={front.stemCx}
                y1={front.yLoadTop}
                x2={front.stemCx}
                y2={front.yStemTop + 10}
                markerEnd="url(#hf-arrow)"
                stroke="#c62828"
              />
              <text className="hf-tx hf13" x={front.stemCx + 10} y={front.yLoadTop + 8} fill="#c62828">
                V = {fmt(raw.normal_v, 'kN')}
              </text>
            </>
          )}

          {showAnnotations && front.showHb && (
            <>
              <line
                className="hf-ln"
                x1={cx + 10}
                y1={footing.bottom + 12}
                x2={cx + 95}
                y2={footing.bottom + 12}
                markerEnd="url(#hf-arrow)"
                stroke="#2e7d32"
              />
              <text className="hf-tx hf12" x={cx + 100} y={footing.bottom + 16} fill="#2e7d32">
                H<tspan baselineShift="sub">B</tspan> = {fmt(raw.hb, 'kN')}
              </text>
            </>
          )}

          <text className="hf-tx hf12 hf-muted" x={VW - xInset - 10} y={yGround + 14} textAnchor="end">
            Front View
          </text>
          <line className="hf-ln hf-muted" x1={VW - xInset - 53} y1={yGround + 51} x2={VW - xInset - 23} y2={yGround + 51} markerEnd="url(#hf-arrow)" />
          <line className="hf-ln hf-muted" x1={VW - xInset - 53} y1={yGround + 51} x2={VW - xInset - 53} y2={yGround + 29} markerEnd="url(#hf-arrow)" />
          <text className="hf-tx hf12 hf-muted" x={VW - xInset - 19} y={yGround + 55}>
            x
          </text>
          <text className="hf-tx hf12 hf-muted" x={VW - xInset - 59} y={yGround + 27} textAnchor="end">
            z
          </text>
          <text className="hf-tx hf12 hf-muted" x={cx - 310} y={yGround + 14}>
            Ground Surface
          </text>
          {showAnnotations && (
            <g>
              <rect x={statusBadge.x} y={statusBadge.y} width={statusBadge.w} height={statusBadge.h} rx="6" fill={statusBadge.fill} stroke={statusBadge.stroke} />
              <text
                className="hf-tx hf9"
                x={statusBadge.x + statusBadge.w / 2}
                y={statusBadge.y + statusBadge.h / 2}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={statusBadge.stroke}
              >
                {statusBadge.text}
              </text>
            </g>
          )}
          {showAnnotations && front.isStemDisplayClipped && (
            <g>
              <rect x={clipBadge.x} y={clipBadge.y} width={clipBadge.w} height={clipBadge.h} rx="6" fill={clipBadge.fill} stroke={clipBadge.stroke} />
              <text
                className="hf-tx hf9"
                x={clipBadge.x + clipBadge.w / 2}
                y={clipBadge.y + clipBadge.h / 2}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={clipBadge.stroke}
              >
                {clipBadge.text}
              </text>
            </g>
          )}

          {front.noInputs && (
            <HansenSketchEmptyState VW={VW} VH={VH} theme={theme} textClassName="hf-tx hf13" />
          )}
        </svg>
      </div>
    </div>
  );
}
